/**
 * KAN-648: окно "Видео и 2FA" карточки аккаунта: ссылка на видео, загрузка
 * файла (video/*, до 200 МБ), удаление файла и признак 2FA при создании.
 * Ссылка и 2FA уходят одним PATCH {id}/, файл отдельно на POST {id}/video/.
 * Состояние формы живёт внутри DialogContent и сбрасывается при закрытии.
 */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { AccountVideoFields } from '@/components/telegram/AccountVideoFields';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import {
  choiceToTwoFa,
  isVideoDraftValid,
  twoFaToChoice,
  type TwoFaChoice,
  type VideoDraft,
} from '@/lib/accountVideo';
import { describeApiError } from '@/lib/telegramConnect';
import { telegramAccountsApi } from '@/services/api/telegramAccounts';
import type { TelegramAccount } from '@/types/telegramAccount';

const QUERY_KEY = ['telegram-accounts'];
const TWO_FA_OPTIONS = [
  { value: 'unknown', label: 'Неизвестно' },
  { value: 'yes', label: 'Да, был пароль 2FA' },
  { value: 'no', label: 'Нет, без 2FA' },
];

interface Props {
  account: TelegramAccount;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function Body({
  account,
  onClose,
}: {
  account: TelegramAccount;
  onClose: () => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const key = `${account.operator_id}-${account.account_id}`;
  const [draft, setDraft] = useState<VideoDraft>({
    url: account.video_url ?? '',
    file: null,
  });
  const [twoFa, setTwoFa] = useState<TwoFaChoice>(
    twoFaToChoice(account.has_2fa_at_creation)
  );
  const [error, setError] = useState<string | null>(null);
  const refresh = () => queryClient.invalidateQueries({ queryKey: QUERY_KEY });

  const save = useMutation({
    mutationFn: async () => {
      await telegramAccountsApi.updateAccount(
        account.account_id,
        { video_url: draft.url.trim(), has_2fa_at_creation: choiceToTwoFa(twoFa) },
        account.operator_id
      );
      if (draft.file) {
        await telegramAccountsApi.uploadVideo(
          account.account_id,
          draft.file,
          account.operator_id
        );
      }
    },
    onSuccess: () => {
      void refresh();
      toast.success('Видео сохранено');
      onClose();
    },
    onError: (err) => setError(describeApiError(err, 'Не удалось сохранить видео')),
  });

  const removeFile = useMutation({
    mutationFn: () =>
      telegramAccountsApi.deleteVideo(account.account_id, account.operator_id),
    onSuccess: () => {
      void refresh();
      toast.success('Файл видео удалён');
    },
    onError: (err) => setError(describeApiError(err, 'Не удалось удалить файл')),
  });

  const busy = save.isPending || removeFile.isPending;
  return (
    <div className="space-y-4">
      <AccountVideoFields
        value={draft}
        onChange={setDraft}
        disabled={busy}
        idPrefix={`card-video-${key}`}
      />
      {account.has_video_file && (
        <div className="flex items-center justify-between text-sm text-gray-700">
          <span>Файл видео загружен</span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={busy}
            data-testid={`card-video-delete-${key}`}
            onClick={() => removeFile.mutate()}
          >
            <Trash2 className="mr-1 h-3 w-3" />
            Удалить файл
          </Button>
        </div>
      )}
      <div className="space-y-1">
        <Label htmlFor={`card-two-fa-${key}`}>2FA при создании</Label>
        <NativeSelect
          id={`card-two-fa-${key}`}
          data-testid={`card-two-fa-${key}`}
          value={twoFa}
          disabled={busy}
          options={TWO_FA_OPTIONS}
          onChange={(e) => setTwoFa(e.target.value as TwoFaChoice)}
        />
      </div>
      {error && (
        <p className="text-sm text-red-600" data-testid={`card-video-error-${key}`}>
          {error}
        </p>
      )}
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose} disabled={busy}>
          Отмена
        </Button>
        <Button
          type="button"
          data-testid={`card-video-save-${key}`}
          disabled={busy || !isVideoDraftValid(draft)}
          onClick={() => {
            setError(null);
            save.mutate();
          }}
        >
          {save.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Сохранить
        </Button>
      </DialogFooter>
    </div>
  );
}

export function AccountVideoDialog({
  account,
  open,
  onOpenChange,
}: Props): JSX.Element {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="account-video-dialog">
        <DialogHeader>
          <DialogTitle>Видео и 2FA</DialogTitle>
          <DialogDescription>
            Видеозапись подключения аккаунта и признак пароля двухэтапной проверки.
          </DialogDescription>
        </DialogHeader>
        {open && <Body account={account} onClose={() => onOpenChange(false)} />}
      </DialogContent>
    </Dialog>
  );
}
